import { useState, useEffect } from 'react';
import API from '../api';

// ── 사번설정 모달 ──────────────────────────────────────
// 인사관리 > 조직도 화면의 "🏢 조직구성" 버튼 옆 "🔢 사번설정" 버튼에서 연다(HRManagement.js
// OrgChartTab 참고). 요청사항: "조직도 - 조직구성 버튼옆에 사번설정 버튼추가 / 사번설정: 부서별로
// 사번기준을 설정할 수 있도록 구성 / 설정된 사번은 직원등록에 적용".
//
// 2026-08 추가 — "접두어를 두종류로 나눔: 1) 회사약어(회사별 하나만 설정) 2) 부서약어(부서별
// 개별설정)". 최종 사번 = 회사약어 + 부서약어 + 순번(자릿수만큼 0으로 채움). 회사약어는 전
// 회사에 딱 하나만 설정하는 값이라 이 모달 상단에 별도 입력칸으로 분리했고, 부서별 표는
// "부서약어"(예전 "접두어") 컬럼만 갖는다.
//
// 2026-08 추가 — "부서약어 뒤에 입사년도 뒤 두자리 자동반영 (예: NIC25001 = 회사약어+부서약어+
// 입사년도+순번)". 이 화면은 규칙 설정 미리보기용이라 실제 입사일이 아직 없으므로 현재 연도의
// 뒤 2자리를 보여준다(실제 채번 시점에는 백엔드가 사원등록 폼의 입사일 연도를 사용한다).
//
// 2026-08 추가 — "닫기버튼 왼쪽에 저장 버튼을 넣어서 일괄저장할 수 있게": 예전에는 부서별로
// 행마다 "저장" 버튼을 눌러야 했는데, 이제 하단에 "💾 일괄저장" 버튼 하나로 회사약어 + 화면에
// 입력된 모든 부서 규칙을 한 번에 저장한다(백엔드: PATCH /org/departments/empno-rules/bulk).
//
// 2026-08 버그수정 — 예전에는 부모(OrgChartTab)가 GET /hr/org-chart로 불러온 departments를
// prop으로 그대로 받아 seed했는데, 그 API는 조직도 캔버스 렌더링용이라 id/name/parent_id/좌표만
// 내려주고 empno_prefix/empno_digits/empno_next 컬럼은 아예 포함하지 않는다 — 그래서 이 모달을
// 다시 열 때마다 이미 저장해둔 값이 안 보이고 항상 "미설정" 기본값(자릿수 4, 다음번호 1)으로
// 되돌아가 보이는 문제가 있었다. 이제 이 모달이 열릴 때 직접 GET /org(SELECT * FROM
// departments — 전체 컬럼 포함)로 다시 불러와 정확한 저장값을 반영한다.
export default function EmpNoRuleModal({ onClose }) {
  const [loading, setLoading] = useState(true);
  const [companyPrefix, setCompanyPrefix] = useState('');
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [msgIsError, setMsgIsError] = useState(false);

  useEffect(() => {
    API.get('/org').then(r => {
      setCompanyPrefix(r.data.empnoCompanyPrefix || '');
      setRows((r.data.departments || []).map(d => ({
        id: d.id,
        name: d.name,
        prefix: d.empno_prefix || '',
        digits: d.empno_digits != null ? String(d.empno_digits) : '4',
        next_seq: d.empno_next != null ? String(d.empno_next) : '1',
        configured: d.empno_digits != null,
      })));
    }).catch(() => {
      setMsg('부서 목록을 불러오지 못했습니다.');
      setMsgIsError(true);
    }).finally(() => setLoading(false));
  }, []);

  const update = (id, patch) => setRows(rs => rs.map(r => (r.id === id ? { ...r, ...patch } : r)));

  const currentYearYY = String(new Date().getFullYear()).slice(-2);
  const preview = (r) => {
    const digits = Number(r.digits);
    const seq = Number(r.next_seq);
    if (!Number.isInteger(digits) || digits < 1 || !Number.isInteger(seq) || seq < 1) return '-';
    return (companyPrefix || '') + (r.prefix || '') + currentYearYY + String(seq).padStart(digits, '0');
  };

  // 일괄저장 — 회사약어 1건 + 부서별 규칙 전체를 한 번의 요청으로 저장한다.
  const saveAll = async () => {
    for (const r of rows) {
      const digits = Number(r.digits);
      const next_seq = Number(r.next_seq);
      if (!Number.isInteger(digits) || digits < 1 || digits > 8) {
        setMsg(`"${r.name}" 부서: 자릿수는 1~8 사이의 숫자로 입력해주세요.`); setMsgIsError(true); return;
      }
      if (!Number.isInteger(next_seq) || next_seq < 1) {
        setMsg(`"${r.name}" 부서: 다음 번호는 1 이상의 숫자로 입력해주세요.`); setMsgIsError(true); return;
      }
    }
    setSaving(true);
    setMsg('');
    try {
      await API.patch('/org/departments/empno-rules/bulk', {
        companyPrefix: companyPrefix.trim(),
        rows: rows.map(r => ({ id: r.id, name: r.name, prefix: r.prefix.trim(), digits: Number(r.digits), next_seq: Number(r.next_seq) })),
      });
      setRows(rs => rs.map(r => ({ ...r, configured: true })));
      setMsg('사번설정이 일괄저장되었습니다.');
      setMsgIsError(false);
    } catch (err) {
      setMsg(err.response?.data?.message || '저장 중 오류가 발생했습니다.');
      setMsgIsError(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.45)',
      display:'flex', alignItems:'center', justifyContent:'center', zIndex:2000 }}
      onClick={onClose}>
      <div style={{ background:'#fff', borderRadius:16, width:760, maxWidth:'92vw', maxHeight:'86vh',
        display:'flex', flexDirection:'column' }}
        onClick={e => e.stopPropagation()}>

        <div style={{ padding:'20px 24px 8px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ fontSize:17, fontWeight:700 }}>🔢 사번설정</div>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:'#aaa' }}>✕</button>
        </div>
        <div style={{ padding:'0 24px 12px', fontSize:12.5, color:'#888', lineHeight:1.5 }}>
          사번 = 회사약어 + 부서약어 + 입사년도(뒤 두자리) + 순번(자릿수만큼 0으로 채움)으로
          구성됩니다. 회사약어는 전체 회사에 하나만 적용되고, 부서약어·자릿수·다음 번호는
          부서마다 따로 설정합니다. 저장된 규칙은 인사관리 &gt; 직원현황 &gt; 사원등록에서 해당
          부서를 선택했을 때 사번 자동부여에 곧바로 적용됩니다.
        </div>
        {msg && (
          <div style={{ margin:'0 24px 10px', padding:'8px 12px', borderRadius:8, fontSize:12.5,
            background: msgIsError ? '#fef2f2' : '#f0fdf4', color: msgIsError ? '#dc2626' : '#16a34a' }}>
            {msg}
          </div>
        )}

        <div style={{ padding:'0 24px 16px' }}>
          <div style={{ fontSize:12.5, fontWeight:700, color:'#555', marginBottom:6 }}>🏢 회사약어 (전체 공통)</div>
          <input value={companyPrefix} onChange={e => setCompanyPrefix(e.target.value)}
            placeholder="예: KOR (전 부서 사번 맨 앞에 공통으로 붙습니다 — 비워두면 붙지 않음)"
            style={{ ...inputSm, width:'100%', padding:'9px 12px' }} />
        </div>

        <div style={{ padding:'0 24px 24px', overflowY:'auto' }}>
          {loading ? (
            <div style={{ color:'#aaa', fontSize:13, padding:30, textAlign:'center' }}>불러오는 중...</div>
          ) : rows.length === 0 ? (
            <div style={{ color:'#aaa', fontSize:13, padding:30, textAlign:'center', border:'1px dashed #e5e7eb', borderRadius:8 }}>
              등록된 부서가 없습니다. 먼저 "🏢 조직구성"에서 부서를 등록해주세요.
            </div>
          ) : (
            <div style={{ border:'1px solid #eee', borderRadius:8 }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr>
                    {['부서', '부서약어', '자릿수', '다음 번호', '미리보기', '상태'].map((h, i) => (
                      <th key={i} style={{ padding:'9px 6px', fontSize:12, color:'#888', borderBottom:'1px solid #eee', textAlign:'center', fontWeight:600, background:'#fafafa' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.id}>
                      <td style={td}>{r.name}</td>
                      <td style={td}>
                        <input value={r.prefix} onChange={e => update(r.id, { prefix: e.target.value })}
                          placeholder="예: DEV" style={{ ...inputSm, width:70, textAlign:'center' }} />
                      </td>
                      <td style={td}>
                        <input type="number" min={1} max={8} value={r.digits}
                          onChange={e => update(r.id, { digits: e.target.value })}
                          style={{ ...inputSm, width:48, textAlign:'center' }} />
                      </td>
                      <td style={td}>
                        <input type="number" min={1} value={r.next_seq}
                          onChange={e => update(r.id, { next_seq: e.target.value })}
                          style={{ ...inputSm, width:70, textAlign:'center' }} />
                      </td>
                      <td style={{ ...td, fontWeight:700, color:'#555', fontFamily:'monospace' }}>{preview(r)}</td>
                      <td style={td}>
                        {r.configured
                          ? <span style={{ padding:'2px 9px', borderRadius:6, fontSize:11, fontWeight:700, background:'#dcfce7', color:'#16a34a' }}>설정됨</span>
                          : <span style={{ padding:'2px 9px', borderRadius:6, fontSize:11, fontWeight:700, background:'#f1f5f9', color:'#888' }}>미설정</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* 요청사항: "닫기버튼 왼쪽에 저장 버튼을 넣어서 일괄저장할 수 있게" — 일괄저장 버튼을
            왼쪽, 닫기 버튼을 오른쪽에 배치한다. */}
        <div style={{ padding:'0 24px 20px', display:'flex', gap:8 }}>
          <button onClick={saveAll} disabled={saving || loading}
            style={{ flex:1, padding:'11px 0', background:'#B45309', color:'#fff',
              border:'none', borderRadius:8, cursor: (saving || loading) ? 'default' : 'pointer',
              fontSize:14, fontWeight:600, opacity: (saving || loading) ? 0.6 : 1 }}>
            {saving ? '저장 중...' : '💾 일괄저장'}
          </button>
          <button onClick={onClose}
            style={{ flex:1, padding:'11px 0', background:'#f4f4f5', color:'#555',
              border:'none', borderRadius:8, cursor:'pointer', fontSize:14, fontWeight:600 }}>
            닫기
          </button>
        </div>
      </div>
    </div>
  );
}

const td = { padding:'8px 6px', fontSize:13, borderBottom:'1px solid #f4f4f4', textAlign:'center' };
const inputSm = { padding:'6px 8px', fontSize:12, border:'1px solid #ddd', borderRadius:6, outline:'none', boxSizing:'border-box' };
